import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import axiosInstance from '@API/axios';

/**
 * BulkImportReviewDialog - Dialog for reviewing and editing imported products before saving
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback when dialog is closed without saving
 * @param {Array} importedProducts - Products imported from Excel
 * @param {Function} onApply - Callback when products are saved, receives saved products with IDs
 */
const BulkImportReviewDialog = ({ open, onClose, importedProducts = [], onApply }) => {
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize products when dialog opens
  useEffect(() => {
    if (open && importedProducts.length > 0) {
      setProducts(importedProducts.map((p, idx) => ({ ...p, tempId: idx })));
      setEditingIndex(null);
      setEditForm({});
      setError(null);
    }
  }, [open, importedProducts]);


  const handleDelete = (tempId) => {
    setProducts(prev => prev.filter(p => p.tempId !== tempId));
  };

  const handleStartEdit = (index) => {
    const product = products[index];
    setEditingIndex(index);
    setEditForm({
      productCucNumber: product.productCucNumber || '',
      productName: product.productName || '',
      isAffected: product.isAffected !== false,
      comment: product.comment || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm({});
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    
    setProducts(prev => prev.map((p, idx) => {
      if (idx === editingIndex) {
        return {
          ...p,
          productCucNumber: editForm.productCucNumber,
          productName: editForm.productName,
          isAffected: editForm.isAffected,
          comment: editForm.comment,
        };
      }
      return p;
    }));
    setEditingIndex(null);
    setEditForm({});
  };

  const handleApply = async () => {
    if (products.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Separate existing products (have productId) from new products (need to be created)
      const existingProducts = products.filter(p => p.productId && !p.isTemporary);
      const newProducts = products.filter(p => !p.productId || p.isTemporary);

      let savedNewProducts = [];
      
      if (newProducts.length > 0) {
        // Save new products to database
        const productsToSave = newProducts.map(p => ({
          cucNumber: p.productCucNumber,
          name: p.productName,
        }));

        const response = await axiosInstance.post('/product/bulk-save', productsToSave);
        savedNewProducts = response.data;
      }

      // Map saved products back with their assessment data
      const finalProducts = [
        ...existingProducts.map(p => ({
          productId: p.productId,
          productCucNumber: p.productCucNumber,
          productName: p.productName,
          isAffected: p.isAffected,
          comment: p.comment,
        })),
        ...savedNewProducts.map((saved, idx) => {
          const original = newProducts[idx];
          return {
            productId: saved.id,
            productCucNumber: saved.cucNumber,
            productName: saved.name,
            isAffected: original.isAffected,
            comment: original.comment,
          };
        }),
      ];

      onApply(finalProducts);
    } catch (err) {
      console.error('Failed to save products:', err);
      setError(err.response?.data?.message || 'Failed to save products. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const newProductsCount = products.filter(p => !p.productId || p.isTemporary).length;
  const existingProductsCount = products.filter(p => p.productId && !p.isTemporary).length;


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Review Imported Products</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {newProductsCount > 0 && (
              <Chip 
                label={`${newProductsCount} new`} 
                size="small" 
                color="warning" 
                variant="outlined" 
              />
            )}
            {existingProductsCount > 0 && (
              <Chip 
                label={`${existingProductsCount} existing`} 
                size="small" 
                color="success" 
                variant="outlined" 
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {products.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No products to review</Typography>
          </Box>
        ) : (
          <Box>
            {/* Table Header */}
            <Box sx={{
              display: 'flex',
              backgroundColor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}>
              <Box sx={{ flex: '0 0 15%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem' }}>CUC Number</Box>
              <Box sx={{ flex: '0 0 25%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem' }}>Product Name</Box>
              <Box sx={{ flex: '0 0 12%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem', textAlign: 'center' }}>Affected</Box>
              <Box sx={{ flex: '0 0 28%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem' }}>Comment</Box>
              <Box sx={{ flex: '0 0 10%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem', textAlign: 'center' }}>Status</Box>
              <Box sx={{ flex: '0 0 10%', p: 1.5, fontWeight: 'bold', fontSize: '0.875rem', textAlign: 'center' }}>Actions</Box>
            </Box>

            {/* Table Body */}
            {products.map((product, index) => (
              <Box
                key={product.tempId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: index % 2 === 1 ? theme.palette.grey[50] : 'white',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}
              >
                {editingIndex === index ? (
                  // Edit mode
                  <>
                    <Box sx={{ flex: '0 0 15%', p: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={editForm.productCucNumber}
                        onChange={(e) => setEditForm(prev => ({ ...prev, productCucNumber: e.target.value }))}
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 25%', p: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={editForm.productName}
                        onChange={(e) => setEditForm(prev => ({ ...prev, productName: e.target.value }))}
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 12%', p: 1, textAlign: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editForm.isAffected}
                            onChange={(e) => setEditForm(prev => ({ ...prev, isAffected: e.target.checked }))}
                            size="small"
                          />
                        }
                        label=""
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 28%', p: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={editForm.comment}
                        onChange={(e) => setEditForm(prev => ({ ...prev, comment: e.target.value }))}
                        multiline
                        maxRows={2}
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 10%', p: 1, textAlign: 'center' }}>
                      <Chip
                        label={product.isTemporary ? 'New' : 'Exists'}
                        size="small"
                        color={product.isTemporary ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 10%', p: 1, textAlign: 'center' }}>
                      <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelEdit}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  // View mode
                  <>
                    <Box sx={{ flex: '0 0 15%', p: 1.5, fontSize: '0.875rem' }}>{product.productCucNumber}</Box>
                    <Box sx={{ flex: '0 0 25%', p: 1.5, fontSize: '0.875rem' }}>{product.productName}</Box>
                    <Box sx={{ flex: '0 0 12%', p: 1.5, textAlign: 'center' }}>
                      {product.isAffected ? (
                        <Chip size="small" label="Yes" color="error" icon={<DoDisturbIcon />} />
                      ) : (
                        <Chip size="small" label="No" color="success" icon={<CheckCircleIcon />} />
                      )}
                    </Box>
                    <Box sx={{ flex: '0 0 28%', p: 1.5, fontSize: '0.875rem' }}>
                      {product.comment || <Typography color="text.secondary" fontSize="0.875rem">-</Typography>}
                    </Box>
                    <Box sx={{ flex: '0 0 10%', p: 1.5, textAlign: 'center' }}>
                      <Chip
                        label={product.isTemporary ? 'New' : 'Exists'}
                        size="small"
                        color={product.isTemporary ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 10%', p: 1.5, textAlign: 'center' }}>
                      <IconButton size="small" color="primary" onClick={() => handleStartEdit(index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(product.tempId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={saving || products.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : `Apply (${products.length} products)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportReviewDialog;
